"use client"

import { useState, useRef, useEffect } from "react"
import { Routes, Route, Link, useLocation, useNavigate } from "react-router-dom"
import { Layout, Menu, Button, theme, Dropdown, Breadcrumb } from "antd"
import { MenuFoldOutlined, MenuUnfoldOutlined, UserOutlined, LogoutOutlined, AppstoreOutlined, FileImageOutlined, SnippetsOutlined, RobotOutlined } from "@ant-design/icons"
import "../assets/styles/AdminPageHome.css"
import { doSignOut } from "../firebase/auth"
import { useAuth } from "../contexts/authContext" // Import the auth context

// Import page components
import UsersPage from "./admin/UsersPage"
import ProductsPage from "./admin/ProductsPage"
import DocumentsPage from "./admin/DocumentsPage"
import LandingPageImages from "./admin/LandingPageImages"
import QuoteProcessingPage from "./admin/QuoteProcessingPage"

const { Header, Sider, Content } = Layout

const AdminPageHome = () => {
  const [collapsed, setCollapsed] = useState(false)
  const { token } = theme.useToken()
  const location = useLocation()
  const navigate = useNavigate()
  const userDropdownRef = useRef(null)

  // Get authentication data from context
  const { userLoggedIn, currentUser } = useAuth()

  // Redirect if not logged in or not an admin
  useEffect(() => {
    if (!userLoggedIn) {
      navigate("/signin")
      return
    }
    if (currentUser?.photoURL !== "admin") {
      // Assuming 'admin' is the photoURL value for admin users
      navigate("/")
      return
    }
    if (location.pathname === "/admin" || location.pathname === "/admin/") {
      navigate("/admin/users")
    }
  }, [userLoggedIn, currentUser, navigate, location.pathname])

  const handleLogout = () => {
    doSignOut()
    navigate("/")
  }

  const userMenuItems = [
    // {
    //   key: "1",
    //   label: "Profile",
    //   icon: <UserOutlined />,
    //   onClick: () => navigate("/admin/profile"),
    // },
    // {
    //   key: "2",
    //   label: "Settings",
    //   icon: <SettingOutlined />,
    //   onClick: () => navigate("/admin/settings"),
    // },
    // {
    //   key: "3",
    //   type: "divider",
    // },
    {
      key: "4",
      label: "Logout",
      icon: <LogoutOutlined />,
      onClick: handleLogout,
    },
  ]

  // Get current path to set active menu item
  const pathname = location.pathname
  let currentPath = "users"

  if (pathname.includes("/admin/products")) {
    currentPath = "products"
  } else if (pathname.includes("/admin/landing-images")) {
    currentPath = "landing-images"
  } else if (pathname.includes("/admin/documents")) {
    currentPath = "documents"
  } else if (pathname.includes("/admin/quote-processing")) {
    currentPath = "quote-processing"
  }

  // Handle menu item click for logout
  const handleMenuClick = (e) => {
    if (e.key === "6") {
      handleLogout()
    }
  }

  // If not logged in, don't render the admin panel
  if (!userLoggedIn) {
    return null
  }

  return (
    <Layout className="admin-layout">
      <Sider trigger={null} collapsible collapsed={collapsed} className="admin-sider" width={250}>
        <div className="logo">{collapsed ? "AP" : "Admin Panel"}</div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[
            currentPath === "users"
              ? "1"
              : currentPath === "products"
              ? "2"
              : currentPath === "landing-images"
              ? "3"
              : currentPath === "documents"
              ? "4"
              : currentPath === "quote-processing"
              ? "5"
              : "1"
          ]}
          onClick={handleMenuClick}
          items={[
            {
              key: "1",
              icon: <UserOutlined />,
              label: <Link to="/admin/users">Users</Link>,
            },
            {
              key: "2",
              icon: <AppstoreOutlined />,
              label: <Link to="/admin/products">Products</Link>,
            },
            {
              key: "3",
              icon: <FileImageOutlined />,
              label: <Link to="/admin/landing-images">Imágenes</Link>,
            },
            {
              key: "4",
              icon: <SnippetsOutlined />,
              label: <Link to="/admin/documents">Documentos</Link>,
            },
            {
              key: "5",
              icon: <RobotOutlined />,
              label: <Link to="/admin/quote-processing">IA Presupuestos</Link>,
            },
            {
              key: "6",
              icon: <LogoutOutlined />,
              label: "Logout",
            },
          ]}
        />
      </Sider>
      <Layout>
        <Header className="admin-header" style={{ background: token.colorBgContainer }}>
          <div className="header-left">
            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              className="trigger-button"
            />
            {/* Search container commented out
            <div className="search-container">
              <Search
                placeholder="Buscar..."
                allowClear
                onSearch={handleSearch}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ width: 250 }}
              />
            </div>
            */}
          </div>
          <div className="header-right">
            {/* Notifications container commented out
            <div className="notifications-container" ref={notificationsRef}>
              <Badge count={notifications.length} className="notification-badge">
                <Button type="text" icon={<BellOutlined />} onClick={() => setNotificationsOpen(!notificationsOpen)} />
              </Badge>
              {notificationsOpen && (
                <div className="notifications-dropdown">
                  <div className="notifications-header">
                    <h3>Notificaciones</h3>
                    <Button type="link" size="small">
                      Marcar todas como leídas
                    </Button>
                  </div>
                  <div className="notifications-list">
                    {notifications.map((notification) => (
                      <div key={notification.id} className="notification-item">
                        <div className="notification-content">
                          <p>{notification.message}</p>
                          <span className="notification-time">{notification.time}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="notifications-footer">
                    <Button type="link" block>
                      Ver todas las notificaciones
                    </Button>
                  </div>
                </div>
              )}
            </div>
            <div className="vertical-divider"></div>
            */}
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight" trigger={["click"]}>
              <div className="user-profile" ref={userDropdownRef}>
                <span className="username">{currentUser?.displayName || "Admin User"}</span>
                {currentUser?.photoURL === "admin" && <span className="user-role">Administrador</span>}
              </div>
            </Dropdown>
          </div>
        </Header>
        <Content className="admin-content">
          <Breadcrumb
            className="admin-breadcrumb"
            items={[{ title: "Admin" }, { title: currentPath.charAt(0).toUpperCase() + currentPath.slice(1) }]}
          />

          <div className="dashboard-content">
            <Routes>
              <Route path="/" element={<UsersPage />} />
              <Route path="/users" element={<UsersPage />} />
              <Route path="/products" element={<ProductsPage />} />
              <Route path="/landing-images" element={<LandingPageImages />} />
              <Route path="/documents" element={<DocumentsPage />} />
              <Route path="/quote-processing" element={<QuoteProcessingPage />} />
            </Routes>
          </div>
        </Content>
      </Layout>
    </Layout>
  )
}

export default AdminPageHome

